/**
 * App entry — react-router v7 createBrowserRouter setup.
 *
 * Route tree:
 *   /login                 — public, LoginPage
 *   (protected)            — ProtectedRoute (no token → /login?next=…; must_rotate → /rotate-password)
 *     /rotate-password     — RotatePasswordPage
 *     (AppShell layout)    — header (Wordmark + LanguageChips + HamburgerMenu)
 *       /                  — Navigate to /positions (Phase 2 — HR landing surface)
 *       /admin/users       — UserManagementPage
 *       /admin/audit       — AuditLogPage
 *       /positions         — PositionTilesDashboard (Plan 02-06)
 *       /templates         — TemplateListPage (Plan 02-06)
 *       /templates/new     — TemplateEditorPage (Plan 02-06)
 *       /templates/:id/edit — TemplateEditorPage (Plan 02-06)
 *       /question-bank     — QuestionBankPage (Plan 02-06)
 *       (Plan 02-07 will add /positions/new, /positions/:id, /positions/:id/wizard,
 *        /positions/:id/activate-success — appended in their own clearly-marked
 *        block below.)
 *
 * On boot, ``hydrate()`` validates any persisted token via /auth/me — invalid
 * tokens are cleared so the user lands on /login cleanly.
 */
import { Suspense, useEffect } from "react";
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
} from "react-router";
import { Toaster } from "sonner";

import { AppShell } from "@/components/layout/AppShell";
import { AuditLogPage } from "@/features/audit/AuditLogPage";
import { LoginPage } from "@/features/auth/LoginPage";
import { RotatePasswordPage } from "@/features/auth/RotatePasswordPage";
import { UserManagementPage } from "@/features/users/UserManagementPage";
import { CandidateDetailPage } from "@/features/admin-candidates/CandidateDetailPage";
import { AdminSettingsPage } from "@/features/admin-settings/AdminSettingsPage";
import { useAuthStore } from "@/lib/auth";
import { ProtectedRoute } from "@/routes/ProtectedRoute";
import { RequireRole } from "@/routes/RequireRole";
import { RouteErrorBoundary } from "@/routes/RouteErrorBoundary";

// === Plan 02-06 lazy route imports (SHELL surfaces) ===
// Position dashboard (Task 1) + Templates list/editor (Task 2) +
// Question bank importer/browser (Task 3).
import { PositionTilesDashboardRoute } from "@/routes/positions";
import { QuestionBankRoute, QuestionEditRoute } from "@/routes/question-bank";
import {
  TemplateEditorRoute,
  TemplateListRoute,
} from "@/routes/templates";
// === end Plan 02-06 imports ===
// === Plan 02-07 lazy route imports (WIZARD surfaces) ===
// PositionWizard + ActivateSuccessScreen + PositionDetailPage.
import {
  ActivateSuccessScreenRoute,
  PositionDetailPageRoute,
  PositionWizardRoute,
} from "@/routes/wizard";
// === end Plan 02-07 imports ===
// === Plan 03-05 lazy route imports (CANDIDATE public surface) ===
// Top-level /c/:positionToken tree — outside ProtectedRoute/AppShell (public,
// sessionStorage Bearer token, NOT admin JWT). CandidateLayout pins i18n to
// position.language (5.0j); RequireCandidateSession guards /info (5.0h).
import CandidateLayout from "@/routes/candidate/CandidateLayout";
import RequireCandidateSession from "@/routes/candidate/RequireCandidateSession";
import {
  CandidateConsentRoute,
  CandidatePersonalInfoRoute,
  Stage1QuestionRoute,
  CandidateConfirmationRoute,
} from "@/routes/candidate";
// === end Plan 03-05 imports ===
// === Plan 04-06 lazy route imports (CANDIDATE STAGE 2 public surface) ===
// Top-level /c/stage2/:stage2Token tree — SEPARATE stage_2_token Bearer (A1, HR-minted),
// outside ProtectedRoute/AppShell. CandidateStage2Layout persists the URL token + pins
// i18n to position.language (5.0j); RequireStage2Session guards /upload (5.0h).
import CandidateStage2Layout from "@/routes/candidate-stage2/CandidateStage2Layout";
import RequireStage2Session from "@/routes/candidate-stage2/RequireStage2Session";
import {
  CandidateStage2ConsentRoute,
  CandidateStage2UploadRoute,
  CandidateStage2ConfirmationRoute,
} from "@/routes/candidate-stage2";
// === end Plan 04-06 imports ===
// === Plan 04-08 lazy route imports (HM finalist surface — hiring_manager gated) ===
import {
  HmFinalistDashboardRoute,
  HmFinalistDetailRoute,
} from "@/routes/hm";
// === end Plan 04-08 imports ===
// === Plan 04-09 lazy route import (super_admin cross-domain overview — OPS-05) ===
import { OverviewRoute } from "@/routes/overview";
// === end Plan 04-09 imports ===

/**
 * RoleLanding — role-aware default landing for "/" (BUG-091, 2026-06-05).
 *
 * Hiring managers own a finalist-only surface and must NEVER land on (or see) the
 * admin position tree — Stage 1 / Stage 2 visibility is forbidden by the HM spec.
 * The previous role-blind `Navigate to="/positions"` dropped every role, HM included,
 * onto the full Posiciones dashboard. HM → /finalists; every other role → /positions.
 */
function RoleLanding() {
  const role = useAuthStore((s) => s.user?.role);
  return (
    <Navigate to={role === "hiring_manager" ? "/finalists" : "/positions"} replace />
  );
}

const router = createBrowserRouter([
  {
    // BUG-085 (2026-06-04): app-wide errorElement. A pathless root route renders an
    // implicit <Outlet/> for all children and acts as their error boundary, so a
    // lazy-chunk import() failure on ANY surface (candidate funnel or admin tree)
    // shows the friendly localized connection-problem screen instead of React
    // Router's default developer error.
    errorElement: <RouteErrorBoundary />,
    children: [
      { path: "/login", element: <LoginPage /> },
  // === Plan 03-05 candidate public surface (top-level, no auth gate) ===
  {
    path: "/c/:positionToken",
    element: <CandidateLayout />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={null}>
            <CandidateConsentRoute />
          </Suspense>
        ),
      },
      {
        element: <RequireCandidateSession />,
        children: [
          {
            path: "info",
            element: (
              <Suspense fallback={null}>
                <CandidatePersonalInfoRoute />
              </Suspense>
            ),
          },
          {
            // Plan 03-06: Stage 1 question loop (Bearer-gated).
            path: "q/:n",
            element: (
              <Suspense fallback={null}>
                <Stage1QuestionRoute />
              </Suspense>
            ),
          },
        ],
      },
      {
        // Plan 03-06: terminal confirmation — PUBLIC (outside RequireCandidateSession).
        // The Bearer token is dead server-side post-submit (D-F); /done reads the
        // reference_number from sessionStorage, so it must NOT require a live session.
        path: "done",
        element: (
          <Suspense fallback={null}>
            <CandidateConfirmationRoute />
          </Suspense>
        ),
      },
    ],
  },
  // === end Plan 03-05 candidate routes ===
  // === Plan 04-06 candidate Stage 2 public surface (top-level, no auth gate) ===
  // SEPARATE stage_2_token Bearer (A1). The URL :stage2Token IS the token — the layout
  // persists it to sessionStorage so the consent GET + guard + uploads can read it.
  {
    path: "/c/stage2/:stage2Token",
    element: <CandidateStage2Layout />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={null}>
            <CandidateStage2ConsentRoute />
          </Suspense>
        ),
      },
      {
        element: <RequireStage2Session />,
        children: [
          {
            // Video upload UX — Bearer-gated (CAN-09..11).
            path: "upload",
            element: (
              <Suspense fallback={null}>
                <CandidateStage2UploadRoute />
              </Suspense>
            ),
          },
        ],
      },
      {
        // Terminal one-way confirmation — PUBLIC (outside RequireStage2Session, reads
        // reference_number from sessionStorage so it survives a refresh, CAN-12).
        path: "done",
        element: (
          <Suspense fallback={null}>
            <CandidateStage2ConfirmationRoute />
          </Suspense>
        ),
      },
    ],
  },
  // === end Plan 04-06 candidate Stage 2 routes ===
  {
    element: <ProtectedRoute />,
    children: [
      { path: "/rotate-password", element: <RotatePasswordPage /> },
      {
        element: <AppShell />,
        children: [
          // Phase 2: default landing was the position-tile dashboard for every role.
          // BUG-091 (2026-06-05): made role-aware — hiring_manager lands on /finalists
          // (its finalist-only surface), all other roles keep /positions. The previous
          // /admin/users redirect remains a discoverable nav target via the HamburgerMenu.
          { path: "/", element: <RoleLanding /> },
          // Defense-in-depth role guard (symmetric counterpart of the /admin/audit
          // RequireRole below — see BUG-062). Backend gates GET /api/admin/users on
          // PERM_USER_LIST = {content_admin, super_admin, hr_manager} (rbac.py); the
          // route was previously unguarded, so hr_recruiter/hiring_manager reached
          // the screen and got a failed query instead of a clean 403 page.
          {
            path: "/admin/users",
            element: (
              <RequireRole
                roles={["content_admin", "super_admin", "hr_manager"]}
              >
                <UserManagementPage />
              </RequireRole>
            ),
          },
          // Plan 02-10a Task 4 (Run3-B): hiring_manager is excluded from /audit
          // per backend audit.py:41 (returns 403 'insufficient_role' for any
          // role not in HR_ROLES + PRIVILEGED). RequireRole gives a graceful
          // page-level 403 surface instead of a useAudit query firing and
          // failing with a generic toast. Defense-in-depth aligned with the
          // canMutate pattern (Task 2): backend remains authoritative, frontend
          // hides the affordance.
          {
            path: "/admin/audit",
            element: (
              <RequireRole
                roles={[
                  "content_admin",
                  "super_admin",
                  "hr_recruiter",
                  "hr_manager",
                ]}
              >
                <AuditLogPage />
              </RequireRole>
            ),
          },
          // Plan 03-07: HR candidate detail. RequireRole mirrors backend
          // admin_candidates ALLOWED_HR_ROLES (hiring_manager excluded — finalist-
          // only in Phase 4). Defense-in-depth; backend remains authoritative.
          {
            path: "/admin/candidates/:id",
            element: (
              <RequireRole
                roles={[
                  "content_admin",
                  "super_admin",
                  "hr_manager",
                  "hr_recruiter",
                ]}
              >
                <CandidateDetailPage />
              </RequireRole>
            ),
          },
          // Plan 03-08: admin settings (Compliance tab — Ley 8968 consent-version
          // editor, D7). RequireRole {content_admin, super_admin} mirrors backend
          // consent_versions.py _READ_ROLES (super_admin reads; content_admin writes —
          // Save button canMutate-gated inside). hr_*/hiring_manager get the graceful
          // page-level 403 surface. Defense-in-depth; backend remains authoritative.
          {
            path: "/admin/settings",
            element: (
              <RequireRole roles={["content_admin", "super_admin"]}>
                <AdminSettingsPage />
              </RequireRole>
            ),
          },
          // === Plan 04-08 routes (HM finalist surface — hiring_manager only) ===
          // RequireRole mirrors backend admin_hm._require_hm (hiring_manager). G8 entry
          // point is the HamburgerMenu "Finalistas" link; back-nav per IMP-055.
          {
            path: "/finalists",
            element: (
              <RequireRole roles={["hiring_manager"]}>
                <Suspense fallback={null}>
                  <HmFinalistDashboardRoute />
                </Suspense>
              </RequireRole>
            ),
          },
          {
            path: "/finalists/:id",
            element: (
              <RequireRole roles={["hiring_manager"]}>
                <Suspense fallback={null}>
                  <HmFinalistDetailRoute />
                </Suspense>
              </RequireRole>
            ),
          },
          // === end Plan 04-08 routes ===
          // === Plan 04-09 route (super_admin cross-domain overview — OPS-05) ===
          // RequireRole mirrors backend admin_overview (super_admin-only 403). G8 entry
          // point is the HamburgerMenu "Resumen" link; terminal-surface per IMP-055.
          {
            path: "/overview",
            element: (
              <RequireRole roles={["super_admin"]}>
                <Suspense fallback={null}>
                  <OverviewRoute />
                </Suspense>
              </RequireRole>
            ),
          },
          // === end Plan 04-09 route ===
          // === Plan 02-06 routes (SHELL — dashboard + templates + question bank) ===
          {
            // BUG-091: hiring_manager excluded from the admin position tree — its
            // finalist-only surface is /finalists. Mirrors /admin/candidates/:id.
            path: "/positions",
            element: (
              <RequireRole
                roles={[
                  "content_admin",
                  "super_admin",
                  "hr_manager",
                  "hr_recruiter",
                ]}
              >
                <Suspense fallback={null}>
                  <PositionTilesDashboardRoute />
                </Suspense>
              </RequireRole>
            ),
          },
          {
            path: "/templates",
            element: (
              <Suspense fallback={null}>
                <TemplateListRoute />
              </Suspense>
            ),
          },
          {
            path: "/templates/new",
            element: (
              <Suspense fallback={null}>
                <TemplateEditorRoute />
              </Suspense>
            ),
          },
          {
            path: "/templates/:id/edit",
            element: (
              <Suspense fallback={null}>
                <TemplateEditorRoute />
              </Suspense>
            ),
          },
          {
            path: "/question-bank",
            element: (
              <Suspense fallback={null}>
                <QuestionBankRoute />
              </Suspense>
            ),
          },
          {
            // Plan 02-10b Task 2 — bank-question edit surface.
            path: "/question-bank/:template_id/:question_id/edit",
            element: (
              <Suspense fallback={null}>
                <QuestionEditRoute />
              </Suspense>
            ),
          },
          // === end Plan 02-06 routes ===
          // === Plan 02-07 routes (WIZARD — wizard + activate-success + detail) ===
          {
            // BUG-091: hiring_manager excluded from the admin position tree.
            path: "/positions/:id/wizard",
            element: (
              <RequireRole
                roles={[
                  "content_admin",
                  "super_admin",
                  "hr_manager",
                  "hr_recruiter",
                ]}
              >
                <Suspense fallback={null}>
                  <PositionWizardRoute />
                </Suspense>
              </RequireRole>
            ),
          },
          {
            // BUG-091: hiring_manager excluded from the admin position tree.
            path: "/positions/:id/activate-success",
            element: (
              <RequireRole
                roles={[
                  "content_admin",
                  "super_admin",
                  "hr_manager",
                  "hr_recruiter",
                ]}
              >
                <Suspense fallback={null}>
                  <ActivateSuccessScreenRoute />
                </Suspense>
              </RequireRole>
            ),
          },
          {
            // BUG-091: hiring_manager excluded — this is the PositionDetailPage with
            // Candidatos / Lista corta / Etapas tabs (Stage 1 / Stage 2 visibility).
            path: "/positions/:id",
            element: (
              <RequireRole
                roles={[
                  "content_admin",
                  "super_admin",
                  "hr_manager",
                  "hr_recruiter",
                ]}
              >
                <Suspense fallback={null}>
                  <PositionDetailPageRoute />
                </Suspense>
              </RequireRole>
            ),
          },
          // === end Plan 02-07 routes ===
        ],
      },
    ],
  },
      // Catch-all → /login (post-401 fallback when the user types a stale URL).
      { path: "*", element: <Navigate to="/login" replace /> },
    ],
  },
]);

export default function App() {
  const hydrate = useAuthStore((s) => s.hydrate);
  useEffect(() => {
    hydrate();
  }, [hydrate]);

  return (
    <>
      <RouterProvider router={router} />
      <Toaster richColors position="top-right" />
    </>
  );
}
